import * as React from 'react';
import type { ScrollDirection } from '../hooks/use-horizontal-scroll';
import { ChevronLeft, ChevronRight, Icon } from '@components/atoms';
import { Button, ButtonVariant } from '@components/molecules';
import { cn } from '@utils';

type ScrollArrowButtonProps = {
	direction: ScrollDirection;
	onClick: () => void;
	/** Relative vertical offset (px) nudging the flex-centered button onto the items' center. */
	top: number;
	disabled?: boolean;
};

const ariaLabels: Record<ScrollDirection, string> = {
	left: 'Scroll left',
	right: 'Scroll right',
};

/**
 * Circular navigation arrow displayed beside a HorizontalScrollList that scrolls
 * the row in the given direction when clicked. Built on the ui Button `secondaryNew`
 * variant (white fill, primary border) shaped into a circle; the Button handles the
 * disabled (dimmed) state when the row cannot scroll further. A relative `top` offset
 * nudges the flex-centered button onto the items' center.
 */
export const ScrollArrowButton: React.VoidFunctionComponent<ScrollArrowButtonProps> = ({
	direction,
	onClick,
	top,
	disabled = false,
}) => {
	return (
		<Button
			type="button"
			variant={ButtonVariant.SECONDARY_NEW}
			iconButton
			onClick={onClick}
			disabled={disabled}
			style={{ top }}
			aria-label={ariaLabels[direction]}
			data-testid={`horizontal-scroll-arrow-${direction}`}
			className={cn('relative h-10 w-10 shrink-0 rounded-full p-0 shadow-sm')}
		>
			<Icon size="xl">{direction === 'left' ? <ChevronLeft /> : <ChevronRight />}</Icon>
		</Button>
	);
};
